import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDemo } from './DemoContext';
import { LAYOUT } from '../constants/layout';

// Define CSS variable names as constants
const CSS_VARS = {
  headerHeight: '--header-height',
  leftSidebar: '--left-sidebar-width',
  rightSidebar: '--right-sidebar-width',
  contentMaxWidth: '--content-max-width',
  minWidth: '--min-width',
  minHeight: '--min-height',
  contentHeight: '--content-height'
};

export const LayoutContext = createContext({
  headerHeight: 0,
  leftSidebarWidth: 0,
  rightSidebarWidth: 0,
  contentMaxWidth: LAYOUT.contentMaxWidth,
  minWidth: LAYOUT.minWidth,
  minHeight: LAYOUT.minHeight,
  measureHeader: () => {},
  measureLeftSidebar: () => {},
  measureRightSidebar: () => {},
});

export function useLayout() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}

export function LayoutProvider({ children }) {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(0);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(0);
  const { isDemoMode } = useDemo();
  
  // Refs to store DOM nodes
  const headerRef = useRef(null);
  const leftSidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);

  // Debug ref to track measurement history
  const measurementHistoryRef = useRef([]);

  const logMeasurement = useCallback((type, node, measurement) => {
    const entry = {
      timestamp: new Date().toISOString(),
      type,
      measurement,
      nodeInfo: node ? {
        id: node.id,
        className: node.className,
        dataset: { ...node.dataset },
        style: {
          width: node.style.width,
          display: window.getComputedStyle(node).display,
          position: window.getComputedStyle(node).position,
          visibility: window.getComputedStyle(node).visibility,
        }
      } : null
    };
    
    console.log('📏 [LayoutContext] Measurement:', entry);
    measurementHistoryRef.current.push(entry);
  }, []);

  // Measure functions that store the node reference
  const measureHeader = useCallback(node => {
    if (node) {
      headerRef.current = node;
      const height = node.getBoundingClientRect().height;
      setHeaderHeight(height);
      logMeasurement('header', node, { height });
    }
  }, [logMeasurement]);

  const measureLeftSidebar = useCallback(node => {
    if (node) {
      leftSidebarRef.current = node;
      const width = node.getBoundingClientRect().width;
      setLeftSidebarWidth(width);
      logMeasurement('leftSidebar', node, { width });
    }
  }, [logMeasurement]);

  const measureRightSidebar = useCallback(node => {
    if (node === null) {
      // Reset case
      rightSidebarRef.current = null;
      setRightSidebarWidth(0);
      logMeasurement('rightSidebar_reset', null, { width: 0 });
      return;
    }
    
    if (node) {
      rightSidebarRef.current = node;
      const width = node.getBoundingClientRect().width;
      setRightSidebarWidth(width);
      logMeasurement('rightSidebar', node, { width });
    }
  }, [logMeasurement]);

  // Effect to update CSS variables whenever measurements change
  useEffect(() => {
    const root = document.documentElement;
    
    // Core layout measurements
    root.style.setProperty(CSS_VARS.headerHeight, `${headerHeight}px`);
    root.style.setProperty(CSS_VARS.leftSidebar, `${leftSidebarWidth}px`);
    root.style.setProperty(CSS_VARS.rightSidebar, `${rightSidebarWidth}px`);
    root.style.setProperty(CSS_VARS.contentHeight, `calc(100vh - ${headerHeight}px)`);
    
    // Layout constraints
    root.style.setProperty(CSS_VARS.contentMaxWidth, LAYOUT.contentMaxWidth);
    root.style.setProperty(CSS_VARS.minWidth, LAYOUT.minWidth);
    root.style.setProperty(CSS_VARS.minHeight, LAYOUT.minHeight);

    // Calculate available content width
    const availableWidth = Math.max(
      parseInt(LAYOUT.minWidth),
      window.innerWidth - leftSidebarWidth - rightSidebarWidth
    );
    
    console.log('🎨 [LayoutContext] Updated layout:', {
      headerHeight: `${headerHeight}px`,
      leftSidebarWidth: `${leftSidebarWidth}px`,
      rightSidebarWidth: `${rightSidebarWidth}px`,
      contentHeight: `calc(100vh - ${headerHeight}px)`,
      availableWidth: `${availableWidth}px`,
      contentMaxWidth: LAYOUT.contentMaxWidth,
      minWidth: LAYOUT.minWidth,
      minHeight: LAYOUT.minHeight
    });
  }, [headerHeight, leftSidebarWidth, rightSidebarWidth]);

  // ResizeObserver setup
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      console.warn('ResizeObserver not supported - falling back to window resize events');
      return;
    }

    const ro = new ResizeObserver(entries => {
      entries.forEach(entry => {
        const node = entry.target;
        const { width, height } = entry.contentRect;
        
        if (node === headerRef.current) {
          setHeaderHeight(height);
          logMeasurement('header_resize', node, { height });
        }
        else if (node === leftSidebarRef.current) {
          setLeftSidebarWidth(width);
          logMeasurement('leftSidebar_resize', node, { width });
        }
        else if (node === rightSidebarRef.current) {
          setRightSidebarWidth(width);
          logMeasurement('rightSidebar_resize', node, { width });
        }
      });
    });

    // Observe elements
    [headerRef, leftSidebarRef, rightSidebarRef].forEach(ref => {
      if (ref.current) ro.observe(ref.current);
    });

    return () => ro.disconnect();
  }, [logMeasurement]);

  // Window resize handler for older browsers and additional checks
  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(() => {
        // Get container dimensions
        const container = document.getElementById('app-layout-root');
        const viewportWidth = container ? container.clientWidth : Math.max(window.innerWidth, parseInt(LAYOUT.minWidth));
        const viewportHeight = container ? container.clientHeight : Math.max(window.innerHeight, parseInt(LAYOUT.minHeight));
        
        // Re-measure elements if needed
        [
          { ref: headerRef, setter: setHeaderHeight, dim: 'height' },
          { ref: leftSidebarRef, setter: setLeftSidebarWidth, dim: 'width' },
          { ref: rightSidebarRef, setter: setRightSidebarWidth, dim: 'width' }
        ].forEach(({ ref, setter, dim }) => {
          if (ref.current) {
            const size = ref.current.getBoundingClientRect()[dim];
            setter(size);
            logMeasurement(`${dim}_window_resize`, ref.current, { [dim]: size });
          }
        });

        console.log('📐 [LayoutContext] Container resized:', { viewportWidth, viewportHeight });
      });
    };

    window.addEventListener('resize', handleResize);
    // Initial measurement
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [logMeasurement]);

  // Demo mode cleanup
  useEffect(() => {
    if (!isDemoMode) {
      console.log('📏 [LayoutContext] Demo ended, resetting measurements');
      
      // Re-measure all elements
      [
        { ref: headerRef, setter: setHeaderHeight, dim: 'height' },
        { ref: leftSidebarRef, setter: setLeftSidebarWidth, dim: 'width' },
        { ref: rightSidebarRef, setter: setRightSidebarWidth, dim: 'width' }
      ].forEach(({ ref, setter, dim }) => {
        if (ref.current) {
          const size = ref.current.getBoundingClientRect()[dim];
          setter(size);
          logMeasurement(`${dim}_demo_cleanup`, ref.current, { [dim]: size });
        }
      });

      // Force resize event to update calculations
      window.dispatchEvent(new Event('resize'));
    }
  }, [isDemoMode, logMeasurement]);

  const value = useMemo(() => ({
    headerHeight,
    leftSidebarWidth,
    rightSidebarWidth,
    contentMaxWidth: LAYOUT.contentMaxWidth,
    minWidth: LAYOUT.minWidth,
    minHeight: LAYOUT.minHeight,
    measureHeader,
    measureLeftSidebar,
    measureRightSidebar,
  }), [
    headerHeight,
    leftSidebarWidth,
    rightSidebarWidth,
    measureHeader,
    measureLeftSidebar,
    measureRightSidebar
  ]);

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
} 